import { APP_ROUTES } from '../lib/routes';
import { UserRole } from './types';

const ROLE_ROUTES: Record<UserRole, string[]> = {
  [UserRole.Business]: [APP_ROUTES.search],
  [UserRole.Technical]: [APP_ROUTES.search]
};

// Discrete things a user can do in the portal. Add a capability here as new actions appear.
export type VehicleCapability = 'reserve' | 'advertising';

// The capabilities each role holds. Business and technical users currently have identical
// rights; this map is the single place to diverge them later (drop a capability from a role).
//
// Scope of this model: it distinguishes roles by WHICH ACTIONS they may perform (action-level,
// role-based). If the roles ever need to differ by DATA (e.g. only their own dealer's vehicles),
// by FIELD (e.g. only certain products), or by STATE (e.g. only while a lead is active), that is
// a different mechanism — extend `can()` to take the resource, don't try to encode it here.
const ROLE_CAPABILITIES: Record<UserRole, ReadonlySet<VehicleCapability>> = {
  [UserRole.Business]: new Set<VehicleCapability>(['reserve', 'advertising']),
  [UserRole.Technical]: new Set<VehicleCapability>(['reserve', 'advertising'])
};

// Whether ANY of the user's roles grants the capability (union across roles, so a user who
// holds both roles gets the combined set — never fewer rights than a single-role user).
export function can(roles: UserRole[], capability: VehicleCapability): boolean {
  return roles.some((role) => ROLE_CAPABILITIES[role].has(capability));
}

interface ParsedAccessToken {
  resource_access?: Record<string, { roles?: string[] }>;
}

export function toUserRoles(roles: string[]): UserRole[] {
  const allowed = new Set(Object.values(UserRole));
  return roles.filter((role): role is UserRole => allowed.has(role as UserRole));
}

export function getPrimaryUserRole(roles: UserRole[]): UserRole | undefined {
  if (roles.includes(UserRole.Business)) {
    return UserRole.Business;
  }

  if (roles.includes(UserRole.Technical)) {
    return UserRole.Technical;
  }

  return undefined;
}

export function getAvailableRoutes(roles: UserRole[]): string[] {
  const routeSet = new Set<string>();

  roles.forEach((role) => {
    ROLE_ROUTES[role].forEach((route) => {
      routeSet.add(route);
    });
  });

  return [...routeSet];
}

export function getTokenUserRoles(tokenParsed: unknown, clientId: string): UserRole[] {
  const parsed = tokenParsed as ParsedAccessToken | undefined;
  const roles = parsed?.resource_access?.[clientId]?.roles ?? [];
  return toUserRoles(roles);
}
